import type { AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import {
  DocumentTypeFields,
  DocumentTypeOption,
  GeneratedDocument,
  GenerateDocumentRequest
} from '@/types/generatedDocument';
import PaginatedResponse from '@/types/paginatedResponse';
import ajax from '@/utils/ajax';

export const generatedDocumentsRepository = {
  async getTypes({ host }: { host: string }): Promise<DocumentTypeOption[]> {
    const response = await ajax.get({ url: `${host}/api/generated_documents/types/` });
    return response.data;
  },

  async getTypeOptions({
    host,
    type,
    personId
  }: {
    host: string;
    type: string;
    personId?: string;
  }): Promise<DocumentTypeFields> {
    const response = await ajax.get({
      url: `${host}/api/generated_documents/types/${type}/options/`,
      params: { person_id: personId }
    });
    return response.data;
  },

  async listForPerson({
    host,
    personId,
    page
  }: {
    host: string;
    personId: string;
    page?: number;
  }): Promise<PaginatedResponse<GeneratedDocument[]>> {
    const response = await ajax.get({
      url: `${host}/api/generated_documents/for_person/${personId}/`,
      params: { page }
    });
    return response.data;
  },

  async create({ host, data }: { host: string; data: GenerateDocumentRequest }): Promise<void> {
    await ajax.post({ url: `${host}/api/generated_documents/`, data });
  },

  async preview({
    host,
    data
  }: {
    host: string;
    data: GenerateDocumentRequest;
  }): Promise<{ data: string }> {
    const response = await ajax.post<GenerateDocumentRequest, AxiosResponse<{ data: string }>>({
      url: `${host}/api/generated_documents/preview/`,
      data
    });
    return response.data;
  }
};

const GeneratedDocumentsRepository = () => {
  const { host } = useContext(AppContext);

  return {
    getTypes: () => generatedDocumentsRepository.getTypes({ host }),
    getTypeOptions: (type: string, personId?: string) =>
      generatedDocumentsRepository.getTypeOptions({ host, type, personId }),
    listForPerson: (personId: string, page?: number) =>
      generatedDocumentsRepository.listForPerson({ host, personId, page }),
    create: (data: GenerateDocumentRequest) => generatedDocumentsRepository.create({ host, data }),
    preview: (data: GenerateDocumentRequest) => generatedDocumentsRepository.preview({ host, data })
  };
};

export default GeneratedDocumentsRepository;
